import dayjs, { Dayjs } from 'dayjs';
import { Order, OrderMonitor } from 'types/orderTypes';
import { DayOfWeek } from '../types/javaTypes';

export function startOfToday(
  today: string | Date | Dayjs = new Date()
): dayjs.Dayjs {
  return dayjs(today).startOf('day');
}

export function startOfDay(date: string | Date | Dayjs): dayjs.Dayjs {
  return dayjs(date).startOf('day');
}

export function isToday(
  date: string | Date | dayjs.Dayjs,
  today = new Date()
): boolean {
  return startOfDay(date).diff(startOfToday(today)) === 0;
}

export function isYesterDay(date: Dayjs): boolean {
  const timeNow = dayjs(new Date());
  const yesterDay = dayjs(timeNow).set('h', -24);
  return startOfDay(date).diff(startOfToday(yesterDay)) === 0;
}

export function isSameDayAsFilterDay(
  date: string | Date | Dayjs,
  filterDay: string | Date | Dayjs
): boolean {
  return (
    dayjs(date).startOf('day').diff(dayjs(filterDay).startOf('day'), 'day') ===
    0
  );
}

export function isAfterNow(date: string | Date | Dayjs): boolean {
  return dayjs(date).diff(dayjs()) >= 0;
}

export function isBeforeNow(
  date: string | Date | Dayjs,
  now: string | Date | Dayjs = new Date()
): boolean {
  return dayjs(date).diff(dayjs(now)) < 0;
}

export function atLeast5minutesFromNow(
  date: string | Date | Dayjs,
  now: string | Date | Dayjs = new Date()
): dayjs.Dayjs {
  const dateToCheck = dayjs(date);
  const nowPlus5min = dayjs(now).add(5, 'minute');
  if (isBeforeNow(dateToCheck, nowPlus5min)) {
    return nowPlus5min;
  } else {
    return dateToCheck;
  }
}

// deviation time functions

export function isBeforeDeliverFrom(
  deliverFrom: string | Date | Dayjs,
  deliveredAt: string | Date | Dayjs
): boolean {
  return dayjs(deliverFrom).diff(deliveredAt, 'minute') > 0;
}

export function isAfterDeliverUntil(
  deliverUntil: string | Date | Dayjs,
  deliveredAt: string | Date | Dayjs
): boolean {
  return dayjs(deliverUntil).diff(deliveredAt, 'minute') < 0;
}

export function isDateWithin2weeksFromNow(date: Dayjs): boolean {
  const today = dayjs(new Date());
  return date.diff(today, 'day') >= 14;
}

export function isDateWithin4weeksFromNow(date: Dayjs): boolean {
  const today = dayjs(new Date());
  return date.diff(today, 'day') <= 28;
}

export function toIsoDate(date: Dayjs): string {
  return date.format('YYYY-MM-DD');
}

export function getInterpolatedDates(start: Dayjs, end: Dayjs): Dayjs[] {
  const emptyDays: null[] = new Array(end.diff(start, 'day') + 1).fill(null);
  return emptyDays.map((_, i) => start.add(i, 'day'));
}

export function javaDayOfWeek(date: Dayjs): DayOfWeek {
  // in dayJs, .day is the day of week, starting with 0 for sunday
  const dayOfWeek = date.day();
  switch (dayOfWeek) {
    case 0:
      return DayOfWeek.Sunday;
    case 1:
      return DayOfWeek.Monday;
    case 2:
      return DayOfWeek.Tuesday;
    case 3:
      return DayOfWeek.Wednesday;
    case 4:
      return DayOfWeek.Thursday;
    case 5:
      return DayOfWeek.Friday;
    case 6:
      return DayOfWeek.Saturday;
  }
}

export function formatOrderWindowInterval(order: Order | OrderMonitor): string {
  return `${dayjs(order.deliverFrom).format('HH:mm')} - ${dayjs(
    order.deliverUntil
  ).format('HH:mm')}`;
}

export function isSameDate(a: Date, b: Date) {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

export function isTimeString(date: Date, timeString: string) {
  const [h, m] = timeString.split(/:/, 2);
  return (
    date.getMinutes() === parseInt(m, 10) && date.getHours() === parseInt(h, 10)
  );
}
